import { useState, useEffect, useCallback, useRef } from 'react';
import { ReportService } from 'api/report.service';
import type { ExportHistoryItem, ExportManagementState, ReportExportJobListItem } from 'types/report';

const ITEMS_PER_PAGE = 20;

export const useExportManagement = () => {
  const [state, setState] = useState<ExportManagementState>({
    exports: [],
    isLoading: true,
    error: null,
    hasNextPage: false,
    isLoadingMore: false,
    totalItems: 0,
    currentLimit: ITEMS_PER_PAGE,
    currentPage: 1,
  });

  const componentMounted = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    return () => {
      componentMounted.current = false;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const convertToHistoryItem = useCallback((job: ReportExportJobListItem): ExportHistoryItem => {
    return {
      jobId: job.job_id,
      status: job.status,
      progress: job.progress,
      reportType: job.report_type,
      format: job.format,
      filename: job.file_name || undefined,
      fileSize: job.file_size || undefined,
      canDownload: job.can_download,
      expiresAt: job.expires_at || undefined,
      createdAt: job.created_at,
      isDownloading: false,
    };
  }, []);

  const loadExports = useCallback(
    async (reset = false) => {
      try {
        const isInitialLoad = reset;

        setState(prev => ({
          ...prev,
          isLoading: isInitialLoad,
          isLoadingMore: !isInitialLoad,
          error: null,
        }));

        setState(prevState => {
          const page = reset ? 1 : prevState.currentPage;
          const limit = prevState.currentLimit;

          ReportService.getReportExportJobs({
            page,
            limit,
          })
            .then(response => {
              if (!componentMounted.current) return;

              const historyItems = response.data.jobs.map(convertToHistoryItem);
              const hasMore = response.pagination.has_next_page;

              setState(prev => ({
                ...prev,
                exports: reset ? historyItems : [...prev.exports, ...historyItems],
                isLoading: false,
                isLoadingMore: false,
                hasNextPage: hasMore,
                totalItems: response.pagination.total_items,
                currentPage: page,
              }));
            })
            .catch(() => {
              if (!componentMounted.current) return;
              setState(prev => ({
                ...prev,
                isLoading: false,
                isLoadingMore: false,
                error: 'Failed to load export history',
              }));
            });

          return prevState;
        });
      } catch {
        if (!componentMounted.current) return;
        setState(prev => ({
          ...prev,
          isLoading: false,
          isLoadingMore: false,
          error: 'Failed to load export history',
        }));
      }
    },
    [convertToHistoryItem]
  );

  // Check if there are any active jobs
  const hasActiveJobs = state.exports.some(job => ['queued', 'processing'].includes(job.status));

  // Single Global SSE Connection - ON DEMAND
  useEffect(() => {
    // Only connect if there are active jobs
    if (!hasActiveJobs) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    const sseUrl = `${baseUrl}/v1/report/export/stream?token=${token}`;

    const connectSSE = () => {
      if (eventSourceRef.current?.readyState === 1) return; // Already connected

      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      eventSource.onmessage = event => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'initial' && data.jobs) {
            // Initial load of active jobs from SSE
            const activeJobs = data.jobs as ReportExportJobListItem[];
            setState(prev => {
              const newExports = prev.exports.map(exp => {
                const match = activeJobs.find(j => j.job_id === exp.jobId);
                if (match) {
                  return { ...exp, status: match.status, progress: match.progress };
                }
                return exp;
              });
              return { ...prev, exports: newExports };
            });
          } else if (data.type === 'update' && data.data) {
            // Handle single job update
            const update = data.data;
            setState(prev => {
              // Check if job exists in our list
              const exists = prev.exports.some(e => e.jobId === update.job_id);

              if (exists) {
                return {
                  ...prev,
                  exports: prev.exports.map(exp =>
                    exp.jobId === update.job_id
                      ? {
                          ...exp,
                          status: update.status,
                          progress: update.progress,
                          filename: update.file_name || exp.filename,
                          fileSize: update.file_size ? String(update.file_size) : exp.fileSize,
                          canDownload: update.download_url ? true : exp.canDownload,
                        }
                      : exp
                  ),
                };
              } else {
                // If it's a new job and we are on the first page, refresh the list
                if (prev.currentPage === 1) {
                  loadExports(true);
                }
                return prev;
              }
            });

            // If completed/failed, we might want to refresh the list to ensure consistency
            if (update.status === 'completed' || update.status === 'failed') {
              // Optional: trigger reload or just let the state update handle it
            }
          }
        } catch {
          // Error parsing SSE message
        }
      };

      eventSource.onerror = () => {
        eventSource.close();
        eventSourceRef.current = null;
        // Retry connection after delay only if active jobs exist
        if (hasActiveJobs) {
          setTimeout(connectSSE, 5000);
        }
      };
    };

    connectSSE();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [hasActiveJobs, loadExports]); // Re-connect if active jobs status changes

  const loadMoreExports = useCallback(() => {
    if (isLoadingMoreRef.current) return;

    setState(prevState => {
      const { isLoadingMore, isLoading, hasNextPage, currentPage, currentLimit } = prevState;

      if (isLoadingMore || isLoading || !hasNextPage) {
        return prevState;
      }

      isLoadingMoreRef.current = true;

      const nextPage = currentPage + 1;

      ReportService.getReportExportJobs({
        page: nextPage,
        limit: currentLimit,
      })
        .then(response => {
          if (!componentMounted.current) {
            isLoadingMoreRef.current = false;
            return;
          }

          const historyItems = response.data.jobs.map(convertToHistoryItem);
          const hasMore = response.pagination.has_next_page;

          setState(prev => ({
            ...prev,
            exports: [...prev.exports, ...historyItems],
            isLoadingMore: false,
            hasNextPage: hasMore,
            totalItems: response.pagination.total_items,
            currentPage: nextPage,
          }));

          isLoadingMoreRef.current = false;
        })
        .catch(() => {
          if (!componentMounted.current) {
            isLoadingMoreRef.current = false;
            return;
          }
          setState(prev => ({ ...prev, isLoadingMore: false }));
          isLoadingMoreRef.current = false;
        });

      return { ...prevState, isLoadingMore: true };
    });
  }, [convertToHistoryItem]);

  const handleDownload = useCallback(async (item: ExportHistoryItem) => {
    if (item.status !== 'completed' || !item.canDownload) return;

    try {
      setState(prev => ({
        ...prev,
        exports: prev.exports.map(exp => (exp.jobId === item.jobId ? { ...exp, isDownloading: true } : exp)),
      }));

      const response = await ReportService.downloadReportExportFile(item.jobId);
      const { download_url, file_name } = response.data;

      const link = document.createElement('a');
      link.href = download_url;
      link.download = file_name || item.filename || `export_${item.jobId}.xlsx`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to download file',
      }));
    } finally {
      setState(prev => ({
        ...prev,
        exports: prev.exports.map(exp => (exp.jobId === item.jobId ? { ...exp, isDownloading: false } : exp)),
      }));
    }
  }, []);

  const handleDelete = useCallback(async (item: ExportHistoryItem) => {
    try {
      setState(prev => ({
        ...prev,
        exports: prev.exports.map(exp => (exp.jobId === item.jobId ? { ...exp, isDeleting: true } : exp)),
      }));

      await ReportService.deleteReportExportJob(item.jobId);

      setState(prev => ({
        ...prev,
        exports: prev.exports.filter(exp => exp.jobId !== item.jobId),
        totalItems: prev.totalItems - 1,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to delete export job',
        exports: prev.exports.map(exp => (exp.jobId === item.jobId ? { ...exp, isDeleting: false } : exp)),
      }));
    }
  }, []);

  const handleRetry = useCallback(() => {
    loadExports(true);
  }, [loadExports]);

  useEffect(() => {
    loadExports(true);
  }, [loadExports]);

  return {
    exports: state.exports,
    isLoading: state.isLoading,
    isLoadingMore: state.isLoadingMore,
    hasNextPage: state.hasNextPage,
    totalItems: state.totalItems,
    currentPage: state.currentPage,
    currentLimit: state.currentLimit,
    error: state.error,
    loadMoreExports,
    handleDownload,
    handleDelete,
    handleRetry,
  };
};

export type UseExportManagementReturn = ReturnType<typeof useExportManagement>;
